const secureStream = require('./secureStream');
const { PLAYBACK_CHECK_TIMEOUT_MS } = require('./verification');

// At most one muted native media probe at a time. Always release the decoder
// and network source; a timeout/unsupported format means unknown, not failed.
function probePlayback(stream, signal, { createVideo = () => document.createElement('video'), timeout = PLAYBACK_CHECK_TIMEOUT_MS } = {}) {
    const url = secureStream(stream)?.url;
    if (!url || !/^https:\/\//i.test(url) || signal.aborted || stream.behaviorHints?.notWebReady ||
        stream.behaviorHints?.proxyHeaders) return Promise.resolve('unknown');
    return new Promise((resolve) => {
        const video = createVideo();
        if (/\.m3u8(?:[?#]|$)/i.test(url) && !video.canPlayType('application/vnd.apple.mpegurl')) {
            resolve('unknown');
            return;
        }
        let done = false;
        let startedAt = null;
        const finish = (result) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            signal.removeEventListener('abort', abort);
            video.removeEventListener('timeupdate', progress);
            video.removeEventListener('error', error);
            video.pause();
            video.removeAttribute('src');
            video.load();
            resolve(result);
        };
        const abort = () => finish('unknown');
        const error = () => finish(video.error?.code === 2 ? 'failed' : 'unknown');
        const progress = () => {
            if (video.paused || video.readyState < 3 || !Number.isFinite(video.currentTime)) return;
            // A supported audio track can advance time even when the browser
            // cannot decode the video track. Require real video output too.
            if (!(video.videoWidth > 0 && video.videoHeight > 0)) return;
            const quality = typeof video.getVideoPlaybackQuality === 'function' ? video.getVideoPlaybackQuality() : null;
            if (quality && !(quality.totalVideoFrames > 1)) return;
            if (startedAt === null) startedAt = video.currentTime;
            if (video.currentTime - startedAt >= 1.5) finish('active');
        };
        const timer = setTimeout(() => finish('unknown'), timeout);
        signal.addEventListener('abort', abort, { once: true });
        video.addEventListener('error', error);
        video.addEventListener('timeupdate', progress);
        video.muted = true;
        video.volume = 0;
        video.playsInline = true;
        video.preload = 'metadata';
        video.src = url;
        try {
            Promise.resolve(video.play()).catch(() => finish('unknown'));
        } catch (_) {
            finish('unknown');
        }
    });
}

const cache = new Map();
const isPlaybackVerified = (stream) => {
    const result = cache.get(secureStream(stream)?.url);
    return result?.state === 'active' && result.expires > Date.now();
};
async function checkPlaybackStreams(streams, signal, probe = probePlayback) {
    const results = [];
    for (const stream of streams) {
        const key = secureStream(stream)?.url;
        const cached = key && cache.get(key);
        let state = 'unknown';
        if (!signal.aborted) {
            if (cached && cached.expires > Date.now()) state = cached.state;
            else {
                try { state = await probe(stream, signal); } catch (_) { state = 'unknown'; }
                if (key && !signal.aborted) {
                    cache.set(key, { state, expires: Date.now() + (state === 'active' ? 120000 : 15000) });
                    while (cache.size > 100) cache.delete(cache.keys().next().value);
                }
            }
        }
        results.push({ stream, state, playbackVerified: state === 'active' });
    }
    return results;
}
module.exports = { probePlayback, checkPlaybackStreams, isPlaybackVerified };
